import type { DateFormatItem, TimeFormatItem, TimeZoneItem } from '../../common/models';

/**
 * What step 2's three format dropdowns show for a row.
 *
 * The platform composes the label (`SiteFormatLabels` on the backend) and every listing carries it as
 * `label`: `DD-MM-YYYY - Day, Month & Four Digit Year`, `HH:mm:ss - 24-hour format`, `IST (+5:30)`. It is
 * used as sent, because the Organization and Entity masters bind their records to exactly that text — a
 * second spelling composed here would be a second source of truth for it.
 *
 * The fallbacks are for a row without one (an older backend): the two format masters compose the same
 * way from the columns they always carried; a time zone's abbreviation and offset cannot be derived
 * reliably in a browser, so it falls back to the zone's name rather than to a guess.
 */

/** Between a pattern and its description, as the platform writes it. */
export const LABEL_SEPARATOR = ' - ';

function clean(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/** `pattern - description`, or whichever half exists. */
export function patternWithDescription(
  pattern: string | null | undefined,
  description: string | null | undefined,
): string {
  const token = clean(pattern);
  const words = clean(description);
  if (token === '') return words;
  return words === '' ? token : `${token}${LABEL_SEPARATOR}${words}`;
}

export function dateFormatLabel(item: DateFormatItem): string {
  return clean(item.label) || patternWithDescription(item.datePattern, item.dateFormat);
}

export function timeFormatLabel(item: TimeFormatItem): string {
  return clean(item.label) || patternWithDescription(item.timePattern, item.timeFormat);
}

export function timeZoneLabel(item: TimeZoneItem): string {
  return clean(item.label) || clean(item.timeZone) || clean(item.timezoneIdentifier);
}
